import React, { useEffect, useState } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { CodeChip, tokens } from "../../shared/clinical-design";

/**
 * Buscador genérico de códigos (CPT o ICD-10) para ingreso manual al charge
 * slip. Debounce de 250ms contra la función `lookup` y lista de resultados
 * clickeable. El padre decide qué hacer al elegir (`onPick`).
 *
 * @param {string}   placeholder
 * @param {Function} lookup  - (query) => Promise<rows>
 * @param {Function} onPick  - (row) => void (async ok)
 * @param {Function} getCode - (row) => string del CodeChip
 * @param {Function} [getExtra] - (row) => ReactNode extra (ej. chip HCC)
 */
export default function CodeSearchAdd({ placeholder, lookup, onPick, getCode, getExtra }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (query.trim()) lookup(query).then(setResults);
      else setResults([]);
    }, 250);
    return () => clearTimeout(t);
  }, [query, lookup]);

  const handlePick = async (row) => {
    setError(null);
    try {
      await onPick(row);
      setQuery("");
      setResults([]);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        size="small"
        fullWidth
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
      />
      {error && (
        <Typography sx={{ fontSize: 12, color: tokens.crit, mt: 0.5 }}>{error}</Typography>
      )}
      {results.length > 0 && (
        <Box
          sx={{
            border: `1px solid ${tokens.line}`,
            borderRadius: "4px",
            mt: 0.5,
            maxHeight: 190,
            overflowY: "auto",
            bgcolor: tokens.surface,
          }}
        >
          {results.map((row) => (
            <Stack
              key={getCode(row)}
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={() => handlePick(row)}
              sx={{
                px: 1.2,
                py: 0.6,
                cursor: "pointer",
                "&:hover": { bgcolor: tokens.primary50 },
                borderBottom: `1px solid ${tokens.line}`,
                "&:last-child": { borderBottom: 0 },
              }}
            >
              <CodeChip code={getCode(row)} />
              <Typography sx={{ fontSize: 13, flex: 1 }}>{row.description}</Typography>
              {getExtra && getExtra(row)}
            </Stack>
          ))}
        </Box>
      )}
    </Box>
  );
}
